import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError, productImageUrl, BACKEND_URL, API } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { Search, Package, CheckCircle2, Truck, Clock, XCircle, ThumbsUp, ThumbsDown, Camera, FileDown, Upload, ShieldCheck, ShieldAlert, ShieldQuestion, CreditCard } from "lucide-react";
import { toast } from "sonner";

const STATUS_STEPS = [
  { key: "pending", label: "En attente", icon: Clock },
  { key: "processing", label: "En traitement", icon: Package },
  { key: "delivered", label: "Livré", icon: CheckCircle2 },
];

const statusIndex = (s) => STATUS_STEPS.findIndex((x) => x.key === s);

const deliveryImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
};

function PaymentSection({ order, onUpdated }) {
  const [proofFile, setProofFile] = useState(null);
  const [senderName, setSenderName] = useState("");
  const [senderPhone, setSenderPhone] = useState("");
  const [reference, setReference] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { format: formatGdes } = useCurrency();
  const fileRef = useRef(null);

  const method = order.payment_method;
  const status = order.payment_status;

  if (method === "cash_on_delivery" || method === "real" || status === "not_required") {
    return null;
  }

  const methodLabel = method === "moncash" ? "MonCash" : method === "natcash" ? "NatCash" : method;
  const methodColor = method === "moncash" ? "#E3000F" : "#0052CC";

  const submitProof = async (e) => {
    e.preventDefault();
    if (!proofFile) { toast.error("Ajoutez une capture d'écran du paiement"); return; }
    if (!senderName.trim() || !senderPhone.trim() || !reference.trim()) {
      toast.error("Nom, téléphone et référence sont requis");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", proofFile);
      const qs = new URLSearchParams({
        sender_name: senderName.trim(),
        sender_phone: senderPhone.trim(),
        reference: reference.trim(),
      });
      await api.post(`/orders/track/${order.tracking_number}/payment-proof?${qs.toString()}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Preuve de paiement envoyée. Vérification en cours.");
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = () => {
    const map = {
      pending_proof: { icon: ShieldQuestion, label: "En attente de preuve", cls: "bg-amber-50 text-amber-700 border-amber-200" },
      awaiting_verification: { icon: ShieldQuestion, label: "En cours de vérification", cls: "bg-blue-50 text-blue-700 border-blue-200" },
      verified: { icon: ShieldCheck, label: "Paiement vérifié", cls: "bg-green-50 text-green-700 border-green-200" },
      rejected: { icon: ShieldAlert, label: "Paiement rejeté", cls: "bg-red-50 text-red-700 border-red-200" },
    };
    const s = map[status] || map.pending_proof;
    const Icon = s.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${s.cls}`} data-testid="payment-status-badge">
        <Icon className="w-3.5 h-3.5" /> {s.label}
      </span>
    );
  };

  return (
    <div className="bg-white border rounded-xl p-6" data-testid="payment-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <CreditCard style={{ color: methodColor }} />
          <h3 className="font-bold text-lg" style={{ fontFamily: "Outfit" }}>Paiement {methodLabel}</h3>
        </div>
        {statusBadge()}
      </div>

      {status === "pending_proof" && (
        <>
          <div className="rounded-md p-4 mb-4" style={{ background: `${methodColor}15`, border: `1px solid ${methodColor}55` }}>
            <p className="text-sm mb-1">Envoyez <b>{formatGdes(order.total)}</b> à :</p>
            <p className="font-bold" style={{ color: methodColor }}>
              {method === "moncash" ? "Djonny Floreal" : "Malicia Danaicka Metayer"}
            </p>
            <p className="font-mono text-base font-bold">
              {method === "moncash" ? "+509 4840 2525" : "+509 3219 0716"}
            </p>
          </div>

          <form onSubmit={submitProof} className="space-y-3">
            <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">
              Après paiement, remplissez ce formulaire :
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <input required value={senderName} onChange={(e) => setSenderName(e.target.value)} placeholder="Nom du payeur" className="border rounded-md px-3 py-2 text-sm" data-testid="proof-sender-name" />
              <input required value={senderPhone} onChange={(e) => setSenderPhone(e.target.value)} placeholder="Téléphone utilisé pour le paiement" className="border rounded-md px-3 py-2 text-sm" data-testid="proof-sender-phone" />
              <input required value={reference} onChange={(e) => setReference(e.target.value)} placeholder="ID / N° de référence de la transaction" className="md:col-span-2 border rounded-md px-3 py-2 text-sm" data-testid="proof-reference" />
            </div>
            <div className="border-2 border-dashed rounded-md p-4">
              <input
                ref={fileRef}
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="proof-file-input"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#0052CC]"
                data-testid="proof-file-btn"
              >
                <Upload className="w-4 h-4" />
                {proofFile ? proofFile.name : "Choisir la capture d'écran"}
              </button>
              <p className="text-xs text-gray-500 mt-2">JPG, PNG, WebP ou PDF. Max 8 Mo.</p>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="je-btn-primary disabled:opacity-50"
              data-testid="proof-submit-btn"
            >
              {submitting ? "Envoi…" : "Envoyer la preuve de paiement"}
            </button>
          </form>
        </>
      )}

      {status === "awaiting_verification" && (
        <div className="text-sm text-gray-700">
          <p>Nous avons bien reçu votre preuve de paiement (réf : <b>{order.payment_reference}</b>).</p>
          <p className="mt-1">Notre équipe la vérifie généralement sous quelques heures. Vous recevrez une confirmation par email.</p>
          {order.payment_proof_url && (
            <a
              href={`${BACKEND_URL}${order.payment_proof_url}`}
              target="_blank"
              rel="noreferrer"
              className="text-[#0052CC] underline text-sm mt-2 inline-block"
              data-testid="view-proof-link"
            >
              Voir la preuve envoyée
            </a>
          )}
        </div>
      )}

      {status === "verified" && (
        <div className="text-sm text-green-700 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" />
          Paiement confirmé le {new Date(order.payment_verified_at || order.updated_at).toLocaleDateString("fr-FR")}. Votre commande passe en traitement.
        </div>
      )}

      {status === "rejected" && (
        <div className="text-sm text-red-700">
          <p><b>Paiement rejeté.</b> {order.payment_reject_reason || "La preuve fournie n'a pas pu être validée."}</p>
          <p className="text-xs mt-2 text-gray-600">Contactez-nous au +509 32190716 pour régulariser votre commande.</p>
        </div>
      )}
    </div>
  );
}

function FeedbackForm({ order, onSubmitted }) {
  const [rating, setRating] = useState(null); // 'up' | 'down'
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) {
      toast.error("Choisissez 👍 ou 👎 avant d'envoyer");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/orders/track/${order.tracking_number}/feedback`, { rating, comment });
      toast.success("Merci pour votre avis !");
      onSubmitted(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border rounded-xl p-6" data-testid="delivery-feedback-form">
      <h3 className="font-bold mb-1" style={{ fontFamily: "Outfit" }}>Votre livraison s&apos;est bien passée ?</h3>
      <p className="text-sm text-gray-500 mb-4">Aidez-nous à améliorer notre service en partageant votre ressenti.</p>
      <div className="flex gap-3 mb-4">
        <button
          type="button"
          onClick={() => setRating("up")}
          className={`flex-1 border-2 rounded-xl py-4 flex flex-col items-center gap-1 transition ${rating === "up" ? "border-green-600 bg-green-50 text-green-700" : "border-gray-200 hover:border-green-400"}`}
          data-testid="feedback-thumbs-up"
        >
          <ThumbsUp className="w-6 h-6" />
          <span className="text-sm font-semibold">Satisfait</span>
        </button>
        <button
          type="button"
          onClick={() => setRating("down")}
          className={`flex-1 border-2 rounded-xl py-4 flex flex-col items-center gap-1 transition ${rating === "down" ? "border-red-600 bg-red-50 text-red-700" : "border-gray-200 hover:border-red-400"}`}
          data-testid="feedback-thumbs-down"
        >
          <ThumbsDown className="w-6 h-6" />
          <span className="text-sm font-semibold">Insatisfait</span>
        </button>
      </div>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Partagez votre expérience (optionnel)…"
        rows={3}
        maxLength={1000}
        className="w-full border rounded-md px-3 py-2 text-sm"
        data-testid="feedback-comment"
      />
      <button
        type="submit"
        disabled={submitting}
        className="je-btn-primary mt-3 disabled:opacity-50"
        data-testid="feedback-submit"
      >
        {submitting ? "Envoi…" : "Envoyer mon avis"}
      </button>
    </form>
  );
}

function FeedbackDisplay({ feedback }) {
  const positive = feedback.rating === "up";
  return (
    <div className={`border rounded-xl p-5 ${positive ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`} data-testid="delivery-feedback-display">
      <div className="flex items-center gap-2 mb-2">
        {positive ? (
          <>
            <ThumbsUp className="w-5 h-5 text-green-700" />
            <span className="font-bold text-green-800">Merci pour votre retour positif !</span>
          </>
        ) : (
          <>
            <ThumbsDown className="w-5 h-5 text-red-700" />
            <span className="font-bold text-red-800">Nous sommes désolés de votre expérience.</span>
          </>
        )}
      </div>
      {feedback.comment && (
        <p className="text-sm text-gray-700 italic">« {feedback.comment} »</p>
      )}
    </div>
  );
}

export default function OrderTrackingPage() {
  const { trackingNumber } = useParams();
  const nav = useNavigate();
  const { format: formatGdes } = useCurrency();
  const [search, setSearch] = useState(trackingNumber || "");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchOrder = async (n) => {
    if (!n) return;
    setLoading(true); setError(""); setOrder(null);
    try {
      const { data } = await api.get(`/orders/track/${n}`);
      setOrder(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (trackingNumber) fetchOrder(trackingNumber);
  }, [trackingNumber]);

  const submit = (e) => {
    e.preventDefault();
    if (search.trim()) nav(`/track/${search.trim()}`);
  };

  const idx = order ? statusIndex(order.status) : -1;
  const isDelivered = order?.status === "delivered";

  return (
    <div className="je-container py-10" data-testid="tracking-page">
      <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{fontFamily:"Outfit"}}>Suivi de commande</h1>
      <p className="text-gray-500 text-sm mb-6">Saisissez votre numéro de suivi (ex: JE-XXXXXX-XXXXXX) pour voir l&apos;état de votre commande.</p>
      <form onSubmit={submit} className="flex gap-2 max-w-xl mb-8">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="JE-251204-AB12CD"
          className="flex-1 border rounded-md px-4 py-3"
          data-testid="tracking-input"
        />
        <button type="submit" className="je-btn-blue inline-flex items-center gap-1" data-testid="tracking-search-btn">
          <Search className="w-4 h-4" /> Chercher
        </button>
      </form>

      {loading && <p className="text-gray-500">Recherche...</p>}
      {error && <div className="bg-red-50 text-red-700 p-4 rounded-xl">{error}</div>}

      {order && (
        <div className="space-y-6" data-testid="tracking-result">
          <div className="bg-white border rounded-xl p-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-sm text-gray-500">N° de suivi</p>
                <p className="text-xl font-bold" style={{fontFamily:"Outfit"}}>{order.tracking_number}</p>
              </div>
              <span className={`je-status-pill je-status-${order.status}`}>{STATUS_STEPS.find(s=>s.key===order.status)?.label || order.status}</span>
            </div>
            {order.status !== "cancelled" ? (
              <div className="mt-8 grid grid-cols-3 gap-4 relative">
                <div className="absolute top-5 left-[16%] right-[16%] h-1 bg-gray-200 rounded">
                  <div className="h-full bg-[#0052CC] rounded transition-all" style={{ width: `${idx >= 0 ? (idx) / (STATUS_STEPS.length - 1) * 100 : 0}%` }} />
                </div>
                {STATUS_STEPS.map((s, i) => (
                  <div key={s.key} className="text-center relative z-10">
                    <div className={`w-11 h-11 mx-auto rounded-full flex items-center justify-center ${i <= idx ? "bg-[#0052CC] text-white" : "bg-gray-100 text-gray-400"}`}>
                      <s.icon className="w-5 h-5" />
                    </div>
                    <p className={`text-xs font-semibold mt-2 ${i <= idx ? "text-[#0052CC]" : "text-gray-400"}`}>{s.label}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-6 inline-flex items-center gap-2 bg-red-50 text-red-700 p-3 rounded-md"><XCircle className="w-5 h-5" /> Commande annulée</div>
            )}
            <div className="grid md:grid-cols-2 gap-6 mt-8 text-sm">
              <div>
                <p className="text-gray-500 uppercase tracking-wider text-xs font-semibold mb-1">Livraison</p>
                <p className="font-semibold">{order.shipping_address.full_name}</p>
                <p className="text-gray-700">{order.shipping_address.address}</p>
                <p className="text-gray-700">{order.shipping_address.city}, {order.shipping_address.department}</p>
                <p className="text-gray-700">{order.shipping_address.phone}</p>
              </div>
              <div>
                <p className="text-gray-500 uppercase tracking-wider text-xs font-semibold mb-1">Total</p>
                <p className="text-2xl font-extrabold" style={{fontFamily:"Outfit"}}>{formatGdes(order.total)}</p>
                {order.discount > 0 && <p className="text-xs text-[#B3000C]">Remise: -{formatGdes(order.discount)}</p>}
                <p className="text-xs text-gray-500 mt-1">{order.items.length} article(s)</p>
                <a
                  href={`${API}/orders/track/${order.tracking_number}/invoice`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold text-[#0052CC] hover:underline"
                  data-testid="download-invoice-btn"
                >
                  <FileDown className="w-4 h-4" /> Télécharger la facture PDF
                </a>
              </div>
            </div>
          </div>

          <PaymentSection order={order} onUpdated={() => fetchOrder(order.tracking_number)} />

          {isDelivered && order.delivery_photo && (
            <div className="bg-white border rounded-xl p-6" data-testid="delivery-photo-section">
              <div className="flex items-center gap-2 mb-3">
                <Camera className="w-5 h-5 text-[#0052CC]" />
                <h3 className="font-bold" style={{ fontFamily: "Outfit" }}>Photo de livraison</h3>
              </div>
              <p className="text-xs text-gray-500 mb-3">Photo prise par notre livreur au moment de la remise du colis.</p>
              <img
                src={deliveryImageUrl(order.delivery_photo)}
                alt="Preuve de livraison"
                className="w-full max-w-md rounded-lg border"
                data-testid="delivery-photo-image"
              />
            </div>
          )}

          {isDelivered && (
            order.customer_feedback
              ? <FeedbackDisplay feedback={order.customer_feedback} />
              : <FeedbackForm order={order} onSubmitted={(fb) => setOrder({ ...order, customer_feedback: fb })} />
          )}

          <div className="bg-white border rounded-xl p-6">
            <h3 className="font-bold mb-4" style={{fontFamily:"Outfit"}}>Articles</h3>
            <div className="space-y-3">
              {order.items.map((i) => (
                <div key={i.product_id} className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-[#F3F4F6] rounded flex items-center justify-center overflow-hidden">
                    <img src={productImageUrl(i.image)} alt="" className="max-h-full max-w-full object-contain p-1.5" />
                  </div>
                  <Link to={`/products/${i.product_id}`} className="flex-1 text-sm font-semibold hover:text-[#0052CC]">{i.name}</Link>
                  <span className="text-sm">x{i.quantity}</span>
                  <span className="text-sm font-bold w-28 text-right">{formatGdes(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
